import { css } from 'astroturf';
import React from 'react';

import Link from '../link';

import { currentPolypaneChromeVersion } from '../../data/releaseData';

const styles = css`
  .faq {
    scroll-margin-top: 5rem;
    max-width: 800px;
    margin: 10rem auto 5rem;

    & h3 {
      text-align: center;
      font-weight: 900;
      font-size: clamp(18px, 16px + 4vw, 44px);
      line-height: 1.25;
      margin: auto;
      margin-bottom: 1.5rem;
      margin-top: 0;
      letter-spacing: -0.015em;
      max-width: 60rem;
      color: rgb(10, 21, 48);
    }

    & button {
      color: rgb(0, 121, 177);
      font-size: inherit;
      font-weight: inherit;
      border: 0;
      margin: 0;
      background: none;
    }

    .item + .item {
      border-top: 1px solid rgba(0, 0, 0, 0.1);
    }

    .header {
      padding: 1rem;
      cursor: pointer;
      color: rgb(10, 21, 48);
      font-size: 1.125rem;
      transition: 0.25s ease-in-out color;
      display: flex;
      align-items: center;

      &:hocus {
        color: #000;
      }

      &::marker {
        display: none;
      }

      &::before {
        display: inline-block;
        content: '';
        height: 8px;
        width: 8px;
        margin-right: 12px;
        border-bottom: 2px solid currentColor;
        border-right: 2px solid currentColor;
        transform: rotate(-45deg);
        transition: 0.25s ease-in-out transform;
      }

      details[open] &::before {
        transform: rotate(45deg) translateY(-0.25rem);
      }
    }

    details::details-content {
      overflow: clip;
      text-wrap: pretty;
      interpolate-size: allow-keywords;
      opacity: 0;
      height: 0;
      padding: 0 1rem 0 2.5rem;
      transition:
        opacity 0.25s ease-in-out,
        height 0.25s ease-in-out,
        padding-bottom 0.25s ease-in-out,
        content-visibility 0.25s ease-in-out allow-discrete;
    }

    details[open]::details-content {
      opacity: 1;
      padding: 0rem 1rem 2rem 2.5rem;
      height: auto;
    }
  }
`;

class FAQ extends React.Component {
  render() {
    return (
      <div className={styles.faq} id="faq">
        <h3>Frequently Asked Questions</h3>
        <div>
          {faqs.map((faq) => (
            <details className={styles.item} key={faq.q}>
              <summary className={styles.header}>{faq.q}</summary>
              <div className="childPadding">{faq.a}</div>
            </details>
          ))}
        </div>
      </div>
    );
  }
}

export default FAQ;

const faqs = [
  {
    q: 'What makes Polypane different from a normal browser?',
    a: (
      <>
        Polypane is a browser built specifically for web developers and designers. This makes it distinct from
        general-purpose browsers like Chrome, Firefox, and Safari. Those are all excellent browsers for general
        browsing.
        <br />
        <br /> Polypane is designed specifically for building and testing websites. It's a development browser with
        integrated features like synchronized multi-viewport testing, accessibility auditing, and an extensive set of
        developer tools that general-purpose browsers don't have.
      </>
    ),
  },
  {
    q: 'When should developers use Polypane vs Chrome, Firefox, or Safari?',
    a: (
      <>
        Use Polypane when you're building, testing, and debugging websites. Use Chrome, Firefox, or Safari for your
        general browsing, and to test specific rendering engines (in the case of Firefox and Safari).
        <br />
        <br /> Polypane is purpose-built for building and testing responsive, accessible websites. Think of it this way:
        your IDE is for writing code, Polypane is for building and testing websites, and Chrome/Firefox/Safari are for
        browsing the web and final cross-browser testing.
      </>
    ),
  },
  {
    q: 'Why use Polypane instead of a few extensions?',
    a: (
      <>
        You can get pretty far with extensions, but they're all single features that only test what's currently there,
        when you remember to run them.
        <br />
        <br />
        An extension looks at the page you have open right now. That's only your viewport, your preferred color scheme
        and your device settings. And the bugs aren't found on your own device, because that's the one you{' '}
        <i>already</i> test on.
        <br />
        <br />
        The bugs that make it to production are usually the ones that only show up in a combination, like a forgotten
        variable causing a contrast issue that only appears in dark mode, or a layout that breaks between your iPhone 16
        and an iPhone 13 mini you didn't check, but your customer did.
        <br />
        <br />
        Polypane puts all those states next to each other while you build and proactively flags issues, so you fix them
        while you build.
      </>
    ),
  },
  {
    q: 'What rendering engine does Polypane use?',
    a: (
      <>
        Polypane is based on Chromium version {currentPolypaneChromeVersion}, and is continuously updated as Chromium
        versions get released (We update to the latest or upcoming Chromium version roughly every two months).
      </>
    ),
  },
  {
    q: 'How does Polypane work?',
    a: (
      <>
        Polypane runs multiple Chromium instances of your site, each with its own emulation settings like dark mode,
        touch, slow network etc. It syncs all interactions like scrolling, form filling, hovers, and focus, so you only
        need to interact with one instances to see the effect in all.
        <br />
        <br />
        Edits in the inspector apply to all instances at once, letting you see changes immediately. At the same time,
        Polypane analyzes your CSS, HTML, storage, metadata, and more to automatically lint your entire page and gives
        you feedback on how to improve it.
      </>
    ),
  },

  {
    q: 'Can I test all browsers in Polypane?',
    a: (
      <p>
        No, but also yes. It's not possible to show all rendering engines in a single, cross-platform browser. Under the
        hood Polypane uses the Chromium engine, the same as Chrome, Edge, Brave and many other browsers. It renders
        sites the way 95%+ of your visitors will see them.
        <br />
        <br />
        Polypane can also <em>emulate</em> other browsers like Safari for iOS or Firefox. This means your site's JS will
        think it's running in them and you can use that to test specific fallback logic. The underlying rendering engine
        is still Chromium though, so a real Safari or Firefox <em>can</em> render things differently. <br />
        <br />
        <strong>Even if you use a developer browser, you should still test in multiple rendering engines</strong>.
        That's what <Link to="/portal/">Polypane Portal</Link> lets you do: open the Portal URL in any browser or device
        you have, and all of them are synced with Polypane. Portal is free for all Polypane users.
      </p>
    ),
  },
  {
    q: 'When is Polypane a bad fit?',
    a: (
      <>
        Polypane is a browser built for developers, so it assumes your website is reasonably well built. It does not try
        to compensate for slow code, which becomes noticeable when you run the same slow code simultaneously half a
        dozen times.
        <br />
        <br />
        And because Polypane is based on Chromium, it will not help you with issues specific to Safari or Firefox.
        You'll still want to test in those, which is what
        <Link to="/portal/">Polypane Portal</Link> is for.
      </>
    ),
  },
  {
    q: 'What operating systems does Polypane support?',
    a: (
      <>
        Polypane runs on Windows, Mac and Linux. We have universal builds to support Intel, Apple Silicon and ARM
        processors. For Linux we have both a .deb and .AppImage available.
        <br />
        <br />
        Our Windows builds are signed with an Extended Validation code signing certificate, and our Mac builds are
        signed and notarized by Apple.
      </>
    ),
  },

  {
    q: 'What is your roadmap?',
    a: (
      <>
        We do not publish a public roadmap, but are very responsive to user requests and input. User requests are added
        as votes to our (extensive, 5+ year) internal roadmap and new features are planned according to popularity.
      </>
    ),
  },
  {
    q: 'I have more questions!',
    a: (
      <>
        I mean, that's more of a remark, but you can find much more about Polypane in our <Link to="/docs/">docs</Link>,
        or ask us questions directly in our <i>non-AI, human-operated</i>{' '}
        <button
          className="link-like"
          type="button"
          onClick={() => {
            if (typeof window !== 'undefined' && !!window.$crisp) {
              window.$crisp.push(['do', 'chat:open']);
            } else {
              alert("It seems you're blocking Crisp, our chat provider. Please use one of the other options!");
            }
          }}
        >
          chat widget
        </button>
        , on <a href="https://bsky.app/profile/polypane.app">Bluesky</a>, in our <Link to="/support/">Slack</Link> or by{' '}
        <Link to="/support/">e-mailing us</Link>.
      </>
    ),
  },
];
